import Reveal from "@/components/Reveal";
import ScreenshotFrame from "@/components/ScreenshotFrame";
import { SIMULATOR_URL } from "@/components/Nav";

const STEPS = [
  {
    id: "s0",
    num: "00",
    title: "Sign in",
    body: [
      "The simulator sits behind a sign-in wall. Accounts are provisioned by a QPI Labs administrator on request.",
      <>
        If you were promised access and can&apos;t get in,{" "}
        <a href="mailto:info@qpilabs.ai" data-testid="tutorial-signin-contact" className="text-beam transition-colors hover:underline">
          email us at info@qpilabs.ai
        </a>.
      </>,
    ],
    tip: "Bookmark simulator.qpilabs.com — that is where the Photonics Classifier lives.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/sutw1771_Capture01.JPG",
      alt: "Sign-in screen of the QPI Photonic Simulator",
      caption: "fig. 00 — sign-in · simulator.qpilabs.com",
      capture: "the sign-in screen at simulator.qpilabs.com",
    },
  },
  {
    id: "s1",
    num: "01",
    title: "Load the model & the dataset",
    body: [
      "The first dropdown is the model picker, grouped by dataset: MNIST, Fashion-MNIST and CIFAR-10. Every entry ships pretrained, with its ideal accuracy and parameter count printed right in the list — Linear (784→10) at 92.6% for a quick run, the MLPs (784→64→10, 784→128→10) in the middle, and CIFAR-10 backbones like SmallCNN, ResNet-8, VGG-8 and Mixer-Tiny when you want the pipeline to sweat.",
      "Pick a model and the simulator loads it together with its dataset; the download button next to the dropdown grabs the checkpoint. Every test sample is then run through the photonic pipeline twice — once ideally, once with every non-ideality switched on — and the sample view compares the ideal softmax in grey against the photonic result in blue.",
    ],
    tip: "Start with the small Linear model to learn the knobs, then climb to VGG-8 — the noise levels you chose suddenly matter a lot more.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/keg0ormx_Capture02.JPG",
      alt: "Model and dataset dropdown listing pretrained models with accuracy and parameter count",
      caption: "fig. 01 — model & dataset dropdown · pretrained, with accuracy and parameters",
      capture: "capture the model & dataset dropdown",
    },
  },
  {
    id: "s2",
    num: "02",
    title: "Pick a modulator technology",
    body: [
      "The modulator dropdown lists five technologies, each with its physics printed right underneath: MZM · Thin-film LiNbO₃ (GHz-speed, cos² transfer, coherent-ready), Ring · Si microring (compact, Lorentzian resonance, thermal-drift-dominant), EAM · InP-QCSE (high-BW telecom, logistic absorption, with chirp), PCM · GST cell (non-volatile, 4–6 levels, write-slow read-fast) and DLP · DMD micromirror (kHz refresh, a million mirrors, 1-bit + PWM — integrate for grayscale, incoherent only).",
      "Swap between them and watch the accuracy react instantly. The MZM is the fast, coherent-ready default — keep it biased near quadrature with a small drive to stay linear. The ring is compact and thermally tuned; the EAM trades the curve for bandwidth plus chirp; the PCM cell holds its weight with zero standby power across 4–6 discrete levels; and the DMD is free of analog error, quantizing every weight to bits as the incoherent-only option.",
    ],
    tip: "Five modulators, one model: step through all of them with the noise fixed, and each drop in accuracy is that technology's true price.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/y198k35w_Capture03.JPG",
      alt: "Modulator dropdown listing MZM, Ring, EAM, PCM and DLP/DMD technologies",
      caption: "fig. 02 — modulator dropdown · five technologies, physics included",
      capture: "capture the modulator dropdown",
    },
  },
  {
    id: "s3",
    num: "03",
    title: "Choose the laser source",
    body: [
      "The source dropdown offers four ways to make the light: VCSEL array — narrow-line, coherent, GHz-fast, small array; LED micro-display (LED array) — broadband, incoherent, MHz-slow, 10⁶ pixels; Single Laser · fanout (parallel) — one emitter through a 1:N splitter, same bandwidth, correlated noise; and Single Laser · time-serialized — one modulator pair, N cycles per MAC, slowest but cheapest.",
      "Each route pays a different bill. The VCSEL array keeps every channel coherent and GHz-fast, but costs an emitter per channel. The fanout shares one emitter across the array — bandwidth survives, yet the noise becomes correlated across channels. Time-serializing reuses a single modulator pair over N cycles per MAC: the slowest and cheapest path. And the LED micro-display is the odd one out — a million incoherent pixels, closer to a projector than a laser system.",
      "Set the source power last: 1.0 is nominal — dim it and you trade SNR away, boost it and you buy SNR back with power dissipation.",
    ],
    tip: "Same model, four sources: swap and watch accuracy and throughput react — correlated noise is the fanout's hidden tax.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/8r9oy3rl_Capture04.JPG",
      alt: "Source dropdown listing VCSEL array, LED micro-display, fanout and time-serialized options",
      caption: "fig. 03 — source dropdown · four ways to make the light",
      capture: "capture the source dropdown",
    },
  },
  {
    id: "s4",
    num: "04",
    title: "Go coherent — or stay direct",
    body: [
      "Detection is a two-way toggle. Incoherent · direct detection: a photodiode simply reads |E|² — intensity only, the simplest hardware. Coherent · homodyne with a balanced detector: the signal is mixed with a local oscillator, which makes signed multiplication native — and makes phase noise matter.",
      "Going coherent unlocks its own controls: more LO power lifts the shot-noise SNR, the LO's phase noise σ blurs what it measures, and the balanced detector's CMRR quietly suppresses the laser's RIN — roughly 31.6× at 30 dB, essentially total at 60 dB. That is why the coherent path can beat incoherent detection at the same nominal laser noise.",
    ],
    tip: "Coherent buys you signed weights and a cleaner readout — it rewards a low phase noise σ, which pairs beautifully with a strong CMRR.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/21qbbalj_Capture05.JPG",
      alt: "Detection toggle showing Incoherent direct detection vs Coherent homodyne with balanced detector",
      caption: "fig. 04 — detection toggle · incoherent vs coherent",
      capture: "capture the detection toggle",
    },
  },
  {
    id: "s5",
    num: "05",
    title: "Inject the non-idealities",
    body: [
      "The Photonic Non-Idealities panel is the lab bench — a list of switches to enable individually, each with its own slider and live value: EOM Bit Precision (DAC quantization on the modulator weights), Shot / Detector Noise (additive Gaussian noise on the photodetector reading), Laser RIN (relative intensity noise on each emitter), Source Power (the optical drive scale factor, nominal 1.0), EOM Extinction Ratio (contrast between the max and min of the modulator transfer) and Insertion Loss (optical attenuation through the EOM).",
      "Flip a toggle, drag a slider, and the next inference runs through degraded hardware — the defaults read 8-bit weights, σ = 0.010 detector noise, σ = 0.020 RIN, 20 dB extinction and 1 dB insertion loss. The reset button restores every switch at once.",
    ],
    tip: "Enable one non-ideality at a time to learn its individual price — then switch them all on together for the real hardware story.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/pt8jcujs_Capture06.JPG",
      alt: "Photonic Non-Idealities panel with individually enabled switches for bit precision, detector noise, RIN, source power, extinction ratio and insertion loss",
      caption: "fig. 05 — photonic non-idealities · enable individually",
      capture: "capture the non-idealities panel",
    },
  },
  {
    id: "s6",
    num: "06",
    title: "Run a batch evaluation",
    body: [
      "One sample is an anecdote; a batch is a verdict. Set the number of samples (200 here), hit run batch, and the panel streams them through the noisy pipeline.",
      "The verdict lands in three tiles — IDEAL, PHOTONIC and the Δ between them (96.00%, 96.00%, 0.00% with these settings) — while the rolling-accuracy chart traces both runs as they settle: ideal dashed, photonic in solid cyan. Widen the gap by turning the noise up in the panels above, then close it again by fixing the optics.",
    ],
    tip: "The Δ tile is the headline — it shows exactly what your hardware choices change.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/zmys9em7_image.png",
      alt: "Batch evaluation panel with sample count, run button, ideal/photonic/delta tiles and rolling accuracy chart",
      caption: "fig. 06 — batch evaluation · rolling accuracy, ideal vs photonic",
      capture: "capture the batch evaluation panel",
    },
  },
  {
    id: "s7",
    num: "07",
    title: "Read the throughput dashboard",
    body: [
      "The Throughput & Memory Analysis page reports the four numbers that matter: THROUGHPUT in inferences per second (102 MIPS amortized in this run), EFFECTIVE TOPS counting each MAC as 2 ops (1.596 TOPS), LATENCY / SAMPLE amortized over the batch (9.82 ns) — and the BOTTLENECK, which names what is limiting you right now (here: ADC readout, eating 89% of tile time).",
      "The left side is hardware sizing, and everything is tunable: array width (128 elements), EOM tile output cols (16), modulation rate (10 GHz), photodetector bandwidth (20 GHz), ADC sample rate (5 GHz), DAC weight update rate (500 MHz), weight bit precision (6 bit) and batch size (128) — plus the weights-static-in-modulator switch: ON loads once per batch, OFF reloads every sample (streaming).",
      "Below, the per-layer tile breakdown maps the model onto tiles — the Linear 784→10 layer becomes 7 tiles and 7,840 MACs, with program time of 48.0 ns for 4 tile-loads × 6 bits — the per-tile time budget splits each tile into laser encode / detector integrate / ADC readout, and the throughput-vs-array-width curve traces the trade as you resize the array: inferences per second climb while tiles per inference multiply.",
    ],
    tip: "The BOTTLENECK card tells you which slider to touch — with ADC readout at 89%, the ADC sample rate is the first knob to turn.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/kdtbtwel_image.png",
      alt: "Throughput and Memory Analysis page with throughput, effective TOPS, latency and bottleneck cards plus hardware sizing sliders and tile breakdown",
      caption: "fig. 07 — throughput & memory analysis · 102 MIPS · 1.596 TOPS · 9.82 ns",
      capture: "capture the throughput dashboard",
    },
  },
  {
    id: "s8",
    num: "08",
    title: "Find the sweet spot on the Pareto pages",
    body: [
      "The Cost vs Accuracy Pareto page is where the simulator earns its name — its own tagline: sweep one param · plot accuracy vs throughput · pick the sweet spot. Choose the parameter to sweep (EOM bit precision here, over 2, 3, 4, 5, 6, 7, 8, 10, 12 — editable, comma-separated), fix every other knob in the base config, and run the sweep at 500 samples per point.",
      "Add the optional second sweep and it becomes a 2-D Pareto heatmap — here EOM bit precision × source power, a 9×7 grid of 63 points rendered as a smooth, draggable 3-D surface (bars available too). Color is photonic accuracy, auto-normalised from 11.8% at the collapsed red corner to 99.4% at the blue plateau — and hovering any cell gives you the exact value.",
      "Read the plateau: accuracy is flat from roughly 8 bits upward, so eight bits is already the efficient choice. The sweet spot is the corner that meets your accuracy target at the lowest power and cost — then commit to hardware.",
    ],
    tip: "The left rail lets you swap the model, flip detection and retune the non-idealities — the Pareto surface will answer for every combination.",
    shot: {
      src: "https://customer-assets-jai6qajn.emergentagent.net/job_qpi-landing/artifacts/qd3ljycy_image.png",
      alt: "Cost vs Accuracy Pareto page with a 2-D sweep of EOM bit precision against source power rendered as a 3-D accuracy surface",
      caption: "fig. 08 — 2-D Pareto surface · EOM bit precision × source power",
      capture: "capture the Pareto sweep and heatmap",
    },
  },
];

function scrollToStep(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -96 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
}

export default function Tutorial() {
  return (
    <main className="pt-16" data-testid="tutorial-page">
      <section className="border-b border-white/10 py-20 md:py-28">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-6 text-laser">{"// Simulator Tutorial"}</p>
            <h1 className="max-w-4xl text-5xl font-extrabold tracking-tighter md:text-7xl">
              Walk the light
              <br />
              through.
            </h1>
            <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-zinc-300 md:text-xl">
              Nine steps from sign-in to the compute sweet spot — the core workflow of the QPI
              Photonic Inference Simulator, shown exactly as the GUI presents it. Consider it a
              guided tour of the essentials — the simulator goes well beyond it.
            </p>
            <p className="mt-6 max-w-2xl text-base font-light leading-relaxed text-zinc-400">
              This tutorial walks you through the full photonic inference pipeline — from model
              loading to photonic non-idealities, throughput dashboards, and Pareto optimization.
            </p>
            <p className="mt-5 font-mono text-xs leading-relaxed text-zinc-500">
              <span className="text-beam">WHO THIS IS FOR</span> — This guide is built for
              engineers, researchers, and developers exploring photonic acceleration.
            </p>
            <div className="mt-8 max-w-xl" data-testid="tutorial-learning-goals">
              <p className="eyebrow mb-4">What you will learn</p>
              <ul className="flex flex-col gap-2.5">
                <li className="flex items-baseline gap-3 font-light text-zinc-300">
                  <span className="font-mono text-[11px] font-bold text-laser">01</span>
                  How to run real neural-network models through a photonic-compute abstraction
                </li>
                <li className="flex items-baseline gap-3 font-light text-zinc-300">
                  <span className="font-mono text-[11px] font-bold text-laser">02</span>
                  How photonic non-idealities affect inference
                </li>
                <li className="flex items-baseline gap-3 font-light text-zinc-300">
                  <span className="font-mono text-[11px] font-bold text-laser">03</span>
                  How to interpret throughput and Pareto dashboards
                </li>
              </ul>
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
              9 steps · ~15 minutes
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x grid grid-cols-1 gap-16 lg:grid-cols-[240px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-28" data-testid="tutorial-sidebar">
              <p className="eyebrow mb-6">Chapters</p>
              <nav className="flex flex-col gap-1">
                {STEPS.map((step) => (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => scrollToStep(step.id)}
                    data-testid={`tutorial-sidebar-link-${step.num}`}
                    className="flex items-baseline gap-3 rounded-lg px-3 py-2 text-left text-sm text-zinc-500 transition-colors hover:bg-white/5 hover:text-white"
                  >
                    <span className="font-mono text-[11px] font-bold text-laser">{step.num}</span>
                    {step.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <div>
            {STEPS.map((step, i) => (
              <Reveal key={step.id}>
                <article
                  id={step.id}
                  className={`scroll-mt-28 border-t border-white/10 py-14 first:border-t-0 first:pt-0 md:py-16`}
                  data-testid={`tutorial-step-${step.num}`}
                >
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
                    <div className="md:col-span-5">
                      <span className="font-mono text-sm font-bold text-laser">{step.num}</span>
                      <h2 className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
                        {step.title}
                      </h2>
                      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-600">
                        step {i + 1} of {STEPS.length}
                      </p>
                    </div>
                    <div className="md:col-span-7">
                      {step.body.map((paragraph, j) => (
                        <p key={j} className="mb-5 font-light leading-relaxed text-zinc-400">
                          {paragraph}
                        </p>
                      ))}
                      <div className="mt-8">
                        <ScreenshotFrame
                          testid={`tutorial-shot-${step.num}`}
                          src={step.shot.src}
                          alt={step.shot.alt}
                          caption={step.shot.caption}
                          capture={step.shot.capture}
                        />
                      </div>
                      <p className="mt-5 border-l-2 border-beam/60 pl-4 font-mono text-xs leading-relaxed text-zinc-500">
                        <span className="text-beam">TIP</span> — {step.tip}
                      </p>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24" data-testid="next-steps">
        <div className="container-x">
          <Reveal>
            <div className="clip-frame-sm border border-white/10 bg-panel p-8 md:p-12">
              <p className="eyebrow mb-3 text-laser">After step 08</p>
              <h2 className="text-3xl font-semibold tracking-tighter md:text-4xl">Next steps</h2>
              <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
                {[
                  "Explore more models",
                  "Adjust modulator and laser settings",
                  "Compare coherent vs direct detection",
                  "Experiment with noise levels",
                  <a href="mailto:info@qpilabs.ai" data-testid="tutorial-nextsteps-contact" className="text-beam transition-colors hover:underline">
                    Contact us at info@qpilabs.ai
                  </a>,
                ].map((item, i) => (
                  <li
                    key={i}
                    className="flex items-baseline gap-4 border-b border-white/5 pb-4 font-light text-zinc-300"
                  >
                    <span className="font-mono text-[11px] font-bold text-beam">
                      {`0${i + 1}`}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-28 md:pb-40">
        <div className="container-x">
          <Reveal>
            <div className="clip-frame-sm relative overflow-hidden border border-white/10 bg-panel p-10 text-center md:p-16">
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(50% 60% at 50% 100%, rgba(61,107,255,0.22), transparent 70%)",
                }}
              />
              <div className="relative z-10">
                <h2 className="text-3xl font-semibold tracking-tighter md:text-5xl">
                  Ready when you are.
                </h2>
                <p className="mx-auto mt-5 max-w-md font-light text-zinc-400">
                  The Photonics Classifier is waiting on the other side of the glass.
                </p>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                  <a
                    href={SIMULATOR_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="tutorial-open-simulator"
                    className="btn-laser"
                  >
                    Open the Simulator
                    <span aria-hidden="true">↗</span>
                  </a>
                  <a href="/" data-testid="tutorial-back-home" className="btn-ghost">
                    Back to QPI Labs
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
